import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpXsrfTokenExtractor,
} from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { ErrorHandlerService } from '../services/error-handler/error-handler.service';
import { CALLER_HANDLED_STATUSES } from './caller-handled-statuses';
import { environment } from '../../environments/environment';

const AUTH_ENDPOINTS = [
  '/auth/internal/login',
  '/auth/internal/register',
  '/auth/internal/2fa/send',
  '/auth/internal/2fa/verify',
  '/auth/internal/invitation-token',
];
const SILENT_401_ENDPOINTS = [...AUTH_ENDPOINTS, '/graphql'];

/**
 * Services tiers appelés directement, hors backend eNegoce : la TradeEventGateway pour la
 * consultation des events d'un dossier.
 *
 * Ils sont laissés intacts. Leur envoyer le cookie de session n'aurait aucun sens — ils ne
 * portent pas notre authentification — et forcerait en plus le mode CORS avec identifiants.
 * Surtout, leurs erreurs ne doivent jamais être lues comme une session expirée : sans cette
 * exclusion, une passerelle éteinte déconnecterait l'utilisateur de l'application entière.
 */
function isExternalService(url: string): boolean {
  const base = environment.tradeEventApiUrl;
  return !!base && url.startsWith(base);
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private csrfRefresh$: Observable<any> | null = null;
  private retryQueue$ = Promise.resolve();

  constructor(
    private errorHandler: ErrorHandlerService,
    private tokenExtractor: HttpXsrfTokenExtractor,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Service tiers : ni session, ni CSRF, ni deconnexion sur erreur.
    if (isExternalService(request.url)) {
      return next.handle(request);
    }

    let authRequest = request.clone({ withCredentials: true });

    const isAuthEndpoint = AUTH_ENDPOINTS.some((p) => request.url.includes(p));
    const isMutating = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(request.method);

    if (isMutating && !isAuthEndpoint) {
      const csrfToken = this.tokenExtractor.getToken();
      if (csrfToken) {
        authRequest = authRequest.clone({
          headers: authRequest.headers.set(environment.xHeaderName, csrfToken),
        });
      }
    }

    return next.handle(authRequest).pipe(
      catchError((error) => {
        const isSilent = SILENT_401_ENDPOINTS.some((p) => request.url.includes(p));

        if (error.status === 403 && error.error?.errorCode === 'CSRF_EXPIRED') {
          if (!this.csrfRefresh$) {
            this.csrfRefresh$ = this.errorHandler.refreshCsrfAndRetry().pipe(
              shareReplay(1),
              finalize(() => (this.csrfRefresh$ = null)),
            );
          }

          return this.csrfRefresh$.pipe(
            switchMap(() => from(this.enqueueRetry(request, next))),
            catchError((retryError) => {
              this.errorHandler.handleHttpError(retryError, request.url, isSilent);
              return throwError(() => retryError);
            }),
          );
        }

        if (!request.context.get(CALLER_HANDLED_STATUSES).includes(error.status)) {
          this.errorHandler.handleHttpError(error, request.url, isSilent);
        }
        return throwError(() => error);
      }),
    );
  }
    private enqueueRetry(request: HttpRequest<any>, next: HttpHandler): Promise<HttpEvent<any>> {
    const run = this.retryQueue$.then(() =>
      new Promise<HttpEvent<any>>((resolve, reject) => {
        let retried = request.clone({ withCredentials: true });
        const token = this.tokenExtractor.getToken();
        if (token) {
          retried = retried.clone({ headers: retried.headers.set(environment.xHeaderName, token) });
        }
        next.handle(retried).subscribe({
          next: (event) => resolve(event),
          error: (err) => reject(err),
        });
      }),
    );
    this.retryQueue$ = run.then(() => undefined, () => undefined);
    return run;
  }
}
